import { useState } from "react";
import {
  User,
  BookOpen,
  Pill,
  Activity,
  AlertTriangle,
  Link2,
  Tag,
  FileText,
  ChevronDown,
  ChevronRight,
  Zap,
  ListChecks,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const CATEGORY_LABELS = {
  cardiovasculaire: "Cardiovasculaire",
  respiratoire: "Respiratoire",
  neurologique: "Neurologique",
  psychiatrique: "Psychiatrique",
  endocrinien: "Endocrinien / Métabolique",
  renal: "Rénal",
  hepatique: "Hépatique",
  digestif: "Digestif",
  rhumatologique: "Rhumatologique",
  urologique: "Urologique",
  ophtalmologique: "Ophtalmologique",
  hematologique: "Hématologique",
  oncologique: "Oncologique",
  autres: "Autres",
};

function SubSection({ title, icon: Icon, iconColor = "text-slate-500", children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</span>
      </div>
      {children}
    </div>
  );
}

function NA() {
  return <p className="text-sm text-slate-400 italic py-1">Non disponible</p>;
}

export default function FicheSyntheseMedicale({ analysis, patient }) {
  const [open, setOpen] = useState(false);

  // 1. Caractéristiques Patient
  const age = patient?.age;
  const sex = patient?.sex;
  const weight = patient?.weight;
  const height = patient?.height;
  const dfg = analysis?.patient_dfg;
  const renalStage = analysis?.patient_renal_stage;
  const imc =
    weight && height ? (weight / Math.pow(height / 100, 2)).toFixed(1) : null;
  const sexLabel = sex === "M" ? "Masculin" : sex === "F" ? "Féminin" : null;

  // 2. Antécédents
  const medHistory = patient?.medical_history || [];
  const presentHistory = medHistory
    .map((cat) => ({ ...cat, items: (cat.items || []).filter((it) => it.present) }))
    .filter((cat) => cat.items.length > 0);

  // 3. Médicaments en cours
  const meds = analysis?.medications_with_atc || [];

  // 4. Événements aigus
  const acutes = (patient?.acute_pathologies || []).filter((a) => a.present);
  const therapModifs = analysis?.therapeutic_modifications || [];
  const notRenewed = analysis?.medications_not_renewed || [];

  // 5. Fragilités
  const phase2 = analysis?.phase2_assessment;
  const fragilityCriteria = phase2?.criteria || [];

  // 6. Recommandations HAS / ESC / EMA
  const hasRecos = analysis?.has_recommendations || [];

  // 7. Concordance
  const concordance = analysis?.concordance_issues || [];

  // 8. Interactions médicamenteuses
  const drugInter = analysis?.drug_interactions || [];
  const classDupes = analysis?.class_duplicates || [];

  // 9. Mots-clés (derived)
  const rawKeywords = [];
  presentHistory.forEach((cat) => {
    cat.items.slice(0, 2).forEach((item) => rawKeywords.push(item.id));
  });
  if (phase2?.level) rawKeywords.push(phase2.level);
  if (dfg && Number(dfg) < 60) rawKeywords.push("IRC");
  const keywords = [...new Set(rawKeywords)].slice(0, 10);

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-slate-50 transition-colors text-left"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-violet-600" />
          <span className="font-semibold text-slate-800">Fiche de Synthèse Médicale</span>
          <Badge variant="outline" className="text-xs font-normal">
            9 sections
          </Badge>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>

      {open && (
        <div className="px-5 py-4 bg-white border-t space-y-5">

          {/* 1 — Caractéristiques Patient */}
          <SubSection title="1. Caractéristiques Patient" icon={User} iconColor="text-violet-600">
            {!age && !sexLabel && !weight && !height && !dfg ? (
              <NA />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {age && (
                  <div className="bg-slate-50 rounded p-2 text-sm">
                    <span className="font-medium">Âge :</span> {age} ans
                  </div>
                )}
                {sexLabel && (
                  <div className="bg-slate-50 rounded p-2 text-sm">
                    <span className="font-medium">Sexe :</span> {sexLabel}
                  </div>
                )}
                {weight && (
                  <div className="bg-slate-50 rounded p-2 text-sm">
                    <span className="font-medium">Poids :</span> {weight} kg
                  </div>
                )}
                {height && (
                  <div className="bg-slate-50 rounded p-2 text-sm">
                    <span className="font-medium">Taille :</span> {height} cm
                  </div>
                )}
                {imc && (
                  <div className="bg-slate-50 rounded p-2 text-sm">
                    <span className="font-medium">IMC :</span> {imc} kg/m²
                  </div>
                )}
                {dfg && (
                  <div className="bg-slate-50 rounded p-2 text-sm">
                    <span className="font-medium">DFG :</span> {dfg} mL/min/1.73m²
                  </div>
                )}
                {renalStage && (
                  <div className="bg-slate-50 rounded p-2 text-sm">
                    <span className="font-medium">Stade rénal :</span> {renalStage}
                  </div>
                )}
              </div>
            )}
          </SubSection>

          {/* 2 — Antécédents / Comorbidités */}
          <SubSection
            title="2. Antécédents / Comorbidités"
            icon={BookOpen}
            iconColor="text-blue-600"
          >
            {presentHistory.length === 0 ? (
              <NA />
            ) : (
              <div className="space-y-2">
                {presentHistory.map((cat) => (
                  <div key={cat.category_id} className="bg-slate-50 rounded p-2">
                    <p className="text-xs font-semibold text-slate-600 mb-1">
                      {CATEGORY_LABELS[cat.category_id] || cat.category_id}
                    </p>
                    <ul className="space-y-0.5">
                      {cat.items.map((item) => (
                        <li key={item.id} className="text-sm text-slate-700 flex gap-2">
                          <span className="text-slate-400">•</span>
                          <span>
                            {item.id}
                            {item.since_year && (
                              <span className="text-slate-400 ml-1">(depuis {item.since_year})</span>
                            )}
                            {item.details && (
                              <span className="text-slate-500 ml-1">— {item.details}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </SubSection>

          {/* 3 — Médicaments en cours */}
          <SubSection title="3. Médicaments en cours" icon={Pill} iconColor="text-emerald-600">
            {meds.length === 0 ? (
              <NA />
            ) : (
              <ul className="space-y-1">
                {meds.map((m, i) => (
                  <li key={i} className="text-sm text-slate-700 flex gap-2">
                    <span className="text-slate-400">•</span>
                    <span>
                      {m.name || m.dci || m.denomination || "Médicament inconnu"}
                      {m.atc_code && (
                        <span className="text-slate-400 ml-1 font-mono text-xs">
                          ({m.atc_code})
                        </span>
                      )}
                      {m.dosage && (
                        <span className="text-slate-500 ml-1">— {m.dosage}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SubSection>

          {/* 4 — Événements aigus / Modifications thérapeutiques */}
          <SubSection
            title="4. Événements aigus / Modifications thérapeutiques"
            icon={Activity}
            iconColor="text-amber-600"
          >
            {acutes.length === 0 && therapModifs.length === 0 && notRenewed.length === 0 ? (
              <NA />
            ) : (
              <div className="space-y-3">
                {acutes.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-1">Pathologies aiguës</p>
                    <ul className="space-y-0.5">
                      {acutes.map((a, i) => (
                        <li key={i} className="text-sm text-slate-700 flex gap-2">
                          <span className="text-slate-400">•</span>
                          <span>
                            {a.id}
                            {a.date && (
                              <span className="text-slate-400 ml-1">({a.date})</span>
                            )}
                            {a.details && (
                              <span className="text-slate-500 ml-1">— {a.details}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {therapModifs.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-1">
                      Modifications thérapeutiques
                    </p>
                    <ul className="space-y-1">
                      {therapModifs.map((m, i) => (
                        <li key={i} className="text-sm text-slate-700 flex gap-2">
                          <span className="text-slate-400">•</span>
                          <span>
                            {m.event_reason || "Modification"}
                            {m.event_date && (
                              <span className="text-slate-400 ml-1">({m.event_date})</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {notRenewed.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 mb-1">
                      Médicaments non renouvelés
                    </p>
                    <ul className="space-y-0.5">
                      {notRenewed.map((m, i) => (
                        <li key={i} className="text-sm text-slate-700 flex gap-2">
                          <span className="text-slate-400">•</span>
                          <span>
                            {m.name}
                            {m.last_seen_date && (
                              <span className="text-slate-400 ml-1">
                                (vu en dernier : {m.last_seen_date})
                              </span>
                            )}
                            {m.note && (
                              <span className="text-slate-500 ml-1">— {m.note}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </SubSection>

          {/* 5 — Fragilités identifiées */}
          <SubSection
            title="5. Fragilités identifiées"
            icon={AlertTriangle}
            iconColor="text-orange-500"
          >
            {!phase2 ? (
              <NA />
            ) : (
              <div className="space-y-2">
                {(phase2.level || phase2.score != null) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {phase2.level && (
                      <>
                        <span className="text-sm font-medium text-slate-700">Niveau :</span>
                        <Badge variant="outline" className="text-xs">
                          {phase2.level}
                        </Badge>
                      </>
                    )}
                    {phase2.score != null && (
                      <span className="text-sm text-slate-500">Score : {phase2.score}</span>
                    )}
                  </div>
                )}
                {fragilityCriteria.length > 0 && (
                  <ul className="space-y-0.5">
                    {fragilityCriteria.map((c, i) => (
                      <li key={i} className="text-sm text-slate-700 flex gap-2">
                        <span className="text-slate-400">•</span>
                        <span>
                          {typeof c === "string" ? c : c.label || c.name || c.criterion || JSON.stringify(c)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {!phase2.level && phase2.score == null && fragilityCriteria.length === 0 && (
                  <NA />
                )}
              </div>
            )}
          </SubSection>

          {/* 6 — Recommandations HAS / ESC / EMA */}
          <SubSection
            title="6. Recommandations HAS / ESC / EMA"
            icon={ListChecks}
            iconColor="text-blue-600"
          >
            {hasRecos.length === 0 ? (
              <NA />
            ) : (
              <ul className="space-y-2">
                {hasRecos.map((r, i) => (
                  <li key={i} className="bg-slate-50 rounded p-2 space-y-0.5">
                    {r.category && (
                      <p className="text-xs font-semibold text-blue-700">{r.category}</p>
                    )}
                    <p className="text-sm text-slate-700">{r.recommendation}</p>
                    {(r.source || r.year) && (
                      <p className="text-xs text-slate-400">
                        {r.source}{r.year ? ` (${r.year})` : ""}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </SubSection>

          {/* 7 — Alertes de concordance */}
          <SubSection
            title="7. Concordance antécédents / traitements"
            icon={Link2}
            iconColor="text-red-600"
          >
            {concordance.length === 0 ? (
              <NA />
            ) : (
              <ul className="space-y-2">
                {concordance.map((c, i) => (
                  <li
                    key={i}
                    className="text-sm text-slate-700 bg-slate-50 rounded p-2 space-y-0.5"
                  >
                    <div>
                      <span className="font-medium">{c.antecedent}</span>
                      {c.expected_treatment && (
                        <span className="text-slate-500">
                          {" "}→ traitement attendu : {c.expected_treatment}
                        </span>
                      )}
                      {c.status && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          {c.status}
                        </Badge>
                      )}
                    </div>
                    {c.recommendation && (
                      <p className="text-slate-500 text-xs">{c.recommendation}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </SubSection>

          {/* 8 — Interactions médicamenteuses */}
          <SubSection
            title="8. Interactions médicamenteuses"
            icon={Zap}
            iconColor="text-orange-500"
          >
            {drugInter.length === 0 && classDupes.length === 0 ? (
              <NA />
            ) : (
              <div className="space-y-2">
                {drugInter.map((inter, i) => (
                  <div key={i} className="bg-slate-50 rounded p-2">
                    <p className="text-sm font-semibold text-slate-800">
                      {inter.medication_1}
                      <Zap className="inline w-3 h-3 mx-1 text-amber-500" />
                      {inter.medication_2}
                    </p>
                    {inter.severity && (
                      <p className="text-xs text-slate-500 mt-0.5">{inter.severity}</p>
                    )}
                    {inter.description && (
                      <p className="text-xs text-slate-600 mt-0.5">{inter.description}</p>
                    )}
                  </div>
                ))}
                {classDupes.map((dup, i) => (
                  <div key={`dup-${i}`} className="bg-slate-50 rounded p-2">
                    <p className="text-sm font-semibold text-slate-800">
                      {dup.therapeutic_class || "Duplication de classe"}
                    </p>
                    {dup.medications?.length > 0 && (
                      <p className="text-xs text-slate-600">{dup.medications.join(", ")}</p>
                    )}
                    {dup.message && (
                      <p className="text-xs text-slate-500 mt-0.5">{dup.message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </SubSection>

          {/* 9 — Mots-clés */}
          <SubSection title="9. Mots-clés" icon={Tag} iconColor="text-slate-500">
            {keywords.length === 0 ? (
              <NA />
            ) : (
              <div className="flex flex-wrap gap-2">
                {keywords.map((kw, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">
                    {kw}
                  </Badge>
                ))}
              </div>
            )}
          </SubSection>

        </div>
      )}
    </div>
  );
}
